// A scanned drone, cut to what the handheld can use: its state, the open flag, the five
// latest history lines and the things you do to it. The full history stays on the main
// app's drone page. Actions sit low, where the thumb of the holding hand reaches.

import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../apiHelpers';
import { clearFlag, clearFlagErrorMessage, flagDrone, logMaintenance } from '../../utils/flag-utils';
import { formatTimestamp } from '../../utils/date-utils';
import { KEY_PRIMARY, KEY_QUIET, KEY_TONAL } from '../Assembly/WorkOrderStation';
import { WarningIcon } from '../icons';
import { buzz } from '../shell';
import { canWorkStock } from '../access';
import { FIELD, Notice, ScreenBar, SECTION } from '../ui';
import type { DroneSummary, HistoryLine } from './types';

const KIND_WORD: Record<HistoryLine['kind'], string> = {
  flag: 'Flagged',
  clear: 'Cleared',
  maintenance: 'Maintenance',
  repair: 'Repair',
};

type Load =
  | { step: 'loading' }
  | { step: 'error'; message: string }
  | { step: 'ready'; data: DroneSummary };

interface Props {
  serial: string;
  caps: unknown;
  onBack: () => void;
  onRepair: (summary: DroneSummary) => void;
}

const InlineForm: React.FC<{
  title: string;
  label: string;
  placeholder: string;
  submitLabel: string;
  busy: boolean;
  onSubmit: (text: string) => void;
  onCancel: () => void;
}> = ({ title, label, placeholder, submitLabel, busy, onSubmit, onCancel }) => {
  const [text, setText] = useState('');
  const trimmed = text.trim();
  return (
    <form
      aria-label={title}
      onSubmit={(e) => { e.preventDefault(); if (trimmed && !busy) onSubmit(trimmed); }}
      className="flex flex-col gap-3"
    >
      <label className="block">
        <span className="mb-1 block text-sm text-on-surface-variant">{label}</span>
        <input
          type="text"
          autoFocus
          autoComplete="off"
          enterKeyHint="done"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          className={FIELD}
        />
      </label>
      <div className="flex items-center gap-2">
        <button type="submit" disabled={!trimmed || busy} className={`${KEY_PRIMARY} !min-h-[3rem] flex-1 !text-base`}>
          {busy ? 'Saving…' : submitLabel}
        </button>
        <button type="button" onClick={onCancel} disabled={busy} className={KEY_QUIET}>Cancel</button>
      </div>
    </form>
  );
};

const DronePage: React.FC<Props> = ({ serial, caps, onBack, onRepair }) => {
  const api = useApi();
  const canWork = canWorkStock(caps);
  const [load, setLoad] = useState<Load>({ step: 'loading' });
  const [reloads, setReloads] = useState(0);
  const [form, setForm] = useState<'flag' | 'maintenance' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; n: number } | null>(null);

  useEffect(() => {
    let alive = true;
    api.get(`/scanner/drone/${encodeURIComponent(serial)}/summary`, { silent: true })
      .then((data: DroneSummary) => { if (alive) setLoad({ step: 'ready', data }); })
      .catch((err: any) => {
        if (!alive) return;
        buzz('reject');
        setLoad({
          step: 'error',
          message: err?.status === 404
            ? `No drone ${serial} is on record.`
            : 'The drone did not load. Check your connection and try again.',
        });
      });
    return () => { alive = false; };
  }, [serial, reloads]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = useCallback(async (work: () => Promise<unknown>, done: string, messageOf: (e: any) => string) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      setForm(null);
      setNotice((prev) => ({ text: done, n: (prev?.n ?? 0) + 1 }));
      setReloads((n) => n + 1);
    } catch (err: any) {
      buzz('reject');
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }, []);

  const bar = <ScreenBar title="Drone" onBack={onBack} />;

  if (load.step === 'loading') {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {bar}
        <p role="status" className="px-6 pt-6 text-center text-base text-on-surface-variant">Opening {serial}…</p>
      </div>
    );
  }

  if (load.step === 'error') {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {bar}
        <div className="mx-auto flex w-full max-w-md flex-col gap-3 px-4 pt-4">
          <div role="alert"><Notice tone="miss">{load.message}</Notice></div>
          <button type="button" onClick={() => { setLoad({ step: 'loading' }); setReloads((n) => n + 1); }} className={KEY_TONAL}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const { data } = load;
  const { drone, open_flag: flag, history } = data;
  const flagged = !!flag;
  const facts: [string, string][] = [
    ['Model', drone.model_name || 'Not set'],
    ['Owner', drone.owner_label || 'Not set'],
    ...(drone.status && drone.status !== drone.condition ? [['Status', drone.status] as [string, string]] : []),
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {bar}
      <div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col gap-3 overflow-y-auto px-4 pb-4">
        <section aria-label="Drone details" className="rounded-xl bg-surface-container p-4">
          <div className="flex items-start justify-between gap-3">
            <h2 className="min-w-0 break-all font-mono text-2xl font-semibold tracking-tight text-on-surface">{drone.serialnumber}</h2>
            {drone.condition && (
              <span
                className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm font-medium
                  ${flagged ? 'bg-amber-900/50 text-amber-200' : 'bg-surface-container-highest text-on-surface'}`}
              >
                {flagged && <WarningIcon className="h-4 w-4" />}
                {drone.condition}
              </span>
            )}
          </div>
          <dl className="mt-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-base">
            {facts.map(([k, v]) => (
              <React.Fragment key={k}>
                <dt className="text-on-surface-variant">{k}</dt>
                <dd className="min-w-0 truncate text-on-surface" title={v}>{v}</dd>
              </React.Fragment>
            ))}
          </dl>
        </section>

        {flag && (
          <section aria-label="Open flag" className="flex gap-2 rounded-xl bg-amber-900/40 px-3 py-2.5 text-amber-100">
            <WarningIcon className="mt-0.5 h-5 w-5 shrink-0" />
            <p className="min-w-0 text-base leading-snug">
              <span className="break-words">{flag.descript}</span>
              <span className="block text-sm text-amber-200">
                {formatTimestamp(flag.created_at, 'MMM D')}{flag.by ? `, ${flag.by}` : ''}
              </span>
            </p>
          </section>
        )}

        <section aria-label="Recent history" className="px-1">
          <h2 className="mb-1 text-sm font-semibold text-on-surface-variant">Recent</h2>
          {history.length === 0 ? (
            <p className="text-sm text-on-surface-variant">Nothing logged on this drone yet.</p>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {history.slice(0, 5).map((h, i) => {
                const line = `${KIND_WORD[h.kind] || h.kind}: ${h.text}${h.by ? `, ${h.by}` : ''}`;
                return (
                  <li key={`${h.at}-${i}`} className="flex gap-2 text-sm leading-6">
                    <span className="w-14 shrink-0 whitespace-nowrap font-mono tabular-nums text-on-surface-variant">
                      {formatTimestamp(h.at, 'MMM D')}
                    </span>
                    <span className="min-w-0 truncate text-on-surface" title={line}>{line}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <div className="mt-auto flex flex-col gap-2 pt-2" aria-live="polite">
          {notice && <Notice key={notice.n} tone="ok">{notice.text}</Notice>}
          {error && <div role="alert"><Notice tone="miss">{error}</Notice></div>}

          {!canWork ? (
            <p className={`${SECTION} text-sm text-on-surface-variant`}>
              Repair, flags and maintenance need operations or manufacturing access.
            </p>
          ) : form === 'flag' ? (
            <InlineForm
              title="Flag this drone"
              label="What is wrong with it?"
              placeholder="Motor 3 does not spin"
              submitLabel="Flag it"
              busy={busy}
              onSubmit={(text) => act(() => flagDrone(api, serial, text), `Flagged ${serial}.`, (e) => e?.message || 'The flag was not saved. Try again.')}
              onCancel={() => setForm(null)}
            />
          ) : form === 'maintenance' ? (
            <InlineForm
              title="Log maintenance"
              label="What was done?"
              placeholder="Replaced props"
              submitLabel="Log it"
              busy={busy}
              onSubmit={(text) => act(() => logMaintenance(api, serial, text), 'Maintenance logged.', (e) => e?.message || 'The entry was not saved. Try again.')}
              onCancel={() => setForm(null)}
            />
          ) : (
            <>
              <button type="button" onClick={() => onRepair(data)} className={KEY_PRIMARY}>
                Repair
              </button>
              <div className="grid grid-cols-2 gap-2">
                {flagged ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => act(() => clearFlag(api, serial), `Cleared the flag on ${serial}.`, clearFlagErrorMessage)}
                    className={`${KEY_TONAL} whitespace-nowrap !px-3`}
                  >
                    {busy ? 'Clearing…' : 'Clear flag'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setNotice(null); setError(null); setForm('flag'); }}
                    className={`${KEY_TONAL} whitespace-nowrap !px-3`}
                  >
                    Flag
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setNotice(null); setError(null); setForm('maintenance'); }}
                  className={`${KEY_TONAL} whitespace-nowrap !px-3`}
                >
                  Log maintenance
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DronePage;
